// Base configuration
import { getApiBaseUrl } from "src/config/endpoints";
import { buildAuthHeaders, handleJsonResponse } from "./httpUtils";

const API_BASE_URL = getApiBaseUrl();
// Interfaces
export interface PageInfo {
  page_id: string;
  page_name: string;
  page_access_token: string;
}

export interface InstagramAccount {
  id: string;
  name: string;
}

export interface ExistingAccountDetails {
  facebook_access_token: string;
  facebook_ad_account_id: string;
  facebook_page_name: string;
  instagram_account_name: string;
}

export interface AdAccountsResponse {
  ad_account_ids: string[];
  pages: PageInfo[];
}

export interface TestConnectionParams {
  accessToken: string;
  adAccountId: string;
}

export interface CreateConnectionParams {
  accessToken: string;
  adAccountId: string;
  page_id: string;
  page_name: string;
  page_access_token: string;
  instagram_account_id: string;
  instagram_account_name: string;
  user_id: string | null;
  tenant_id: string | null;
  source: string;
}

// API functions
export const facebookService = {
  // 1. Fetch existing account details
  fetchExistingAccountDetails: async (
    tenant_id: string | null
  ): Promise<any> => {
    const response = await fetch(
      `${API_BASE_URL}/facebook/fetch-existing-account-details?tenant_id=${tenant_id}`,
      {
        method: "POST",
        headers: buildAuthHeaders("api_key"),
      }
    );
    return handleJsonResponse(response);
  },

  // 2. Save token to server
  saveTokenToServer: async (
    token: string,
    userInfo: { name: string; email: string }
  ): Promise<any> => {
    const api_key = localStorage.getItem("api_key");
    const response = await fetch(`${API_BASE_URL}/facebook/receive-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Authorization: `Bearer ${api_key}`,
      },
      body: `access_token=${token}&name=${encodeURIComponent(
        userInfo.name
      )}&email=${encodeURIComponent(userInfo.email)}`,
    });
    return handleJsonResponse(response);
  },

  // 3. Fetch ad accounts and pages
  fetchAdAccounts: async (token: string): Promise<AdAccountsResponse> => {
    const api_key = localStorage.getItem("api_key");
    const response = await fetch(
      `${API_BASE_URL}/facebook/ad-accounts?access_token=${token}`,
      {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${api_key}`,
        },
      }
    );
    return handleJsonResponse(response);
  },

  // 4. Fetch Instagram account
  fetchInstagramAccount: async (
    pageId: string,
    pageAccessToken: string
  ): Promise<{ success: boolean; instagram_account?: InstagramAccount }> => {
    const response = await fetch(
      `${API_BASE_URL}/facebook/get-instagram-account`,
      {
        method: "POST",
        headers: buildAuthHeaders("api_key"),
        body: JSON.stringify({
          page_id: pageId,
          page_access_token: pageAccessToken,
        }),
      }
    );
    return handleJsonResponse(response);
  },

  // 5. Test connection
  testConnection: async (params: TestConnectionParams): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/facebook/test-connection`, {
      method: "POST",
      headers: buildAuthHeaders("api_key"),
      body: JSON.stringify(params),
    });
    return handleJsonResponse(response);
  },

  // 6. Create connection
  createConnection: async (params: CreateConnectionParams): Promise<any> => {
    const response = await fetch(`${API_BASE_URL}/facebook/create-connection`, {
      method: "POST",
      headers: buildAuthHeaders("api_key"),
      body: JSON.stringify(params),
    });
    return handleJsonResponse(response);
  },

  // 7. Remove connection
  removeConnection: async (tenant_id: string | null): Promise<any> => {
    const response = await fetch(
      `${API_BASE_URL}/remove-connection?tenant_id=${tenant_id}&source_name=facebook`,
      {
        method: "POST",
        headers: buildAuthHeaders("api_key"),
      }
    );
    return handleJsonResponse(response);
  },
};

